import { useRef, useState, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';

export interface AnimationClockState {
  /** Current animation time in seconds */
  currentTime: number;
  /** Whether animation is playing */
  isPlaying: boolean;
  /** Playback speed multiplier */
  speed: number;
}

export interface AnimationClockControls {
  /** Get current animation time */
  getCurrentTime: () => number;
  /** Set animation time (for scrubbing) */
  setTime: (time: number) => void;
  /** Play animation */
  play: () => void;
  /** Pause animation */
  pause: () => void;
  /** Toggle play/pause */
  togglePlayPause: () => void;
  /** Set playback speed */
  setSpeed: (speed: number) => void;
  /** Reset time to zero */
  reset: () => void;
  /** Get current state */
  getState: () => AnimationClockState;
}

/**
 * Custom animation clock hook for controllable playback
 *
 * Provides manual control over animation time instead of relying on Three.js clock.
 * This allows for play/pause, scrubbing, and speed control.
 *
 * @param initialSpeed - Initial playback speed (default: 1)
 * @param initialTime - Initial time in seconds (default: 0)
 * @param initialPlaying - Whether to start playing (default: true)
 * @returns Current time and control functions
 */
export function useAnimationClock(
  initialSpeed: number = 1,
  initialTime: number = 0,
  initialPlaying: boolean = true
): [number, AnimationClockControls] {
  const [currentTime, setCurrentTime] = useState(initialTime);
  const timeRef = useRef(initialTime);
  const isPlayingRef = useRef(initialPlaying);
  const speedRef = useRef(initialSpeed);
  const lastFrameTimeRef = useRef<number | null>(null);

  // Update time on each frame when playing
  useFrame((state) => {
    if (!isPlayingRef.current) {
      lastFrameTimeRef.current = null;
      return;
    }

    const currentFrameTime = state.clock.getElapsedTime();

    if (lastFrameTimeRef.current === null) {
      lastFrameTimeRef.current = currentFrameTime;
      return;
    }

    const deltaTime = currentFrameTime - lastFrameTimeRef.current;
    timeRef.current += deltaTime * speedRef.current;
    lastFrameTimeRef.current = currentFrameTime;

    // Update state every frame to trigger re-renders
    setCurrentTime(timeRef.current);
  });

  const controls: AnimationClockControls = useMemo(
    () => ({
      getCurrentTime: () => timeRef.current,

      setTime: (time: number) => {
        timeRef.current = time;
        setCurrentTime(time);
        lastFrameTimeRef.current = null; // Reset frame tracking
      },

      play: () => {
        isPlayingRef.current = true;
        lastFrameTimeRef.current = null; // Reset frame tracking
      },

      pause: () => {
        isPlayingRef.current = false;
        lastFrameTimeRef.current = null;
      },

      togglePlayPause: () => {
        isPlayingRef.current = !isPlayingRef.current;
        lastFrameTimeRef.current = null;
      },

      setSpeed: (speed: number) => {
        speedRef.current = Math.max(0.01, speed); // Prevent zero/negative speed
      },

      reset: () => {
        timeRef.current = 0;
        setCurrentTime(0);
        lastFrameTimeRef.current = null;
      },

      getState: () => ({
        currentTime: timeRef.current,
        isPlaying: isPlayingRef.current,
        speed: speedRef.current,
      }),
    }),
    []
  ); // Empty deps - controls object is stable

  return [currentTime, controls];
}

/**
 * Convert stroke rate (strokes per minute) to stroke duration (seconds)
 *
 * @param strokeRate - Strokes per minute (SPM)
 * @returns Duration of one stroke cycle in seconds
 */
export function strokeRateToStrokeDuration(strokeRate: number): number {
  return 60 / strokeRate;
}

/**
 * Convert stroke duration (seconds) to stroke rate (strokes per minute)
 *
 * @param strokeDuration - Duration of one stroke cycle in seconds
 * @returns Strokes per minute (SPM)
 */
export function strokeDurationToStrokeRate(strokeDuration: number): number {
  return 60 / strokeDuration;
}
